/**
 * tokens.ts — the ONE place the portal decides which token each platform's API gets
 * (docs/architecture/admin-portal-shape.md §4, Bill 2026-10-16).
 *
 * Target: every API receives the shared backend token, audience
 * api://c9897981-56e9-440c-8f2c-e874e2b17e7f, scope access_as_user. pcb-retail-api,
 * pcb-finance-api, pcb-loans-api and pcb-innovation-api all validate it today.
 *
 * Today the tenant blocks user consent, so that token exists only after Ray grants admin
 * consent for this app registration (cc1002aa) to that scope. Until then:
 *   - pcb-retail-api: falls back to the ID token (getMsalToken, the 2026-06-18 workaround).
 *   - pcb-innovation-api (Hub) and any other platform: NOT called. The page shows
 *     "Waiting for sign-in consent (requested from BT)".
 *
 * The backend token is requested silently only (cache + refresh token, no iframe, no
 * redirect): an interactive request would just show the person a consent error page.
 * A failed attempt is remembered for a few minutes so pages don't retry on every call.
 */

import { CacheLookupPolicy, InteractionRequiredAuthError } from '@azure/msal-browser';
import { ensureMsalReady, getMsalToken, msalInstance } from './msal';

export const BACKEND_API_ID = 'c9897981-56e9-440c-8f2c-e874e2b17e7f';
export const BACKEND_SCOPE = `api://${BACKEND_API_ID}/access_as_user`;

export const CONSENT_WAITING_MESSAGE = 'Waiting for sign-in consent (requested from BT)';

export type BackendTokenState = 'unknown' | 'available' | 'consent-missing';

const RETRY_AFTER_MS = 5 * 60 * 1000;

let state: BackendTokenState = 'unknown';
let lastFailureAt = 0;
let inFlight: Promise<string | null> | null = null;
const listeners = new Set<(s: BackendTokenState) => void>();

function setState(next: BackendTokenState): void {
  if (next === state) return;
  state = next;
  for (const l of listeners) l(state);
}

export function backendTokenState(): BackendTokenState {
  return state;
}

export function onBackendTokenState(listener: (s: BackendTokenState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The shared backend token, or null when consent has not been granted (or no one is signed in). */
export function getBackendToken(): Promise<string | null> {
  if (state === 'consent-missing' && Date.now() - lastFailureAt < RETRY_AFTER_MS) {
    return Promise.resolve(null);
  }
  if (!inFlight) {
    inFlight = acquireBackend().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function acquireBackend(): Promise<string | null> {
  await ensureMsalReady();
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [BACKEND_SCOPE],
      account,
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });
    if (!result.accessToken) throw new Error('no access token');
    setState('available');
    return result.accessToken;
  } catch (e) {
    // consent_required / interaction_required (AADSTS65001) is the expected answer until BT grants consent.
    // Anything else (network, throttling) is treated the same way for this call; it is retried later.
    if (!(e instanceof InteractionRequiredAuthError)) {
      // eslint-disable-next-line no-console
      console.warn('[admin-tokens] backend token unavailable:', e instanceof Error ? e.message : String(e));
    }
    lastFailureAt = Date.now();
    setState('consent-missing');
    return null;
  }
}

/** Token for pcb-retail-api: the shared backend token when consent exists, else the ID token. */
export async function getRetailToken(): Promise<string> {
  const backend = await getBackendToken();
  return backend ?? getMsalToken();
}

/** Token for any other platform's API (Hub, later Loans and Finance): backend token or null. */
export function getPlatformToken(): Promise<string | null> {
  return getBackendToken();
}
